/**
 * GuruAvatar — wrapper que muestra foto real del Guru si existe, sino
 * fallback al BrandedPlaceholder con iniciales.
 *
 * Reutilizable en directorio público, perfil de mentor, panel admin,
 * cards internas, etc.
 */
import { BrandedPlaceholder } from '@/components/ui/branded-placeholder';
import { cn } from '@/lib/utils';

interface Props {
  avatarUrl?: string | null;
  fullName?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  /** Si true, agrega ring blanco grueso (para gradient backgrounds) */
  ringOnDark?: boolean;
}

const SIZE_CLASSES: Record<NonNullable<Props['size']>, string> = {
  sm: 'h-10 w-10',
  md: 'h-14 w-14',
  lg: 'h-20 w-20',
  xl: 'h-28 w-28',
};

export function GuruAvatar({
  avatarUrl, fullName, size = 'md', className, ringOnDark = false,
}: Props) {
  const sizeCls = SIZE_CLASSES[size];
  const initials = fullName?.split(' ').map((n) => n[0]).join('').slice(0, 2) ?? '?';
  const ring = ringOnDark ? 'ring-2 ring-white/30' : '';

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={fullName ?? 'Guru'}
        className={cn('rounded-full object-cover flex-shrink-0', sizeCls, ring, className)}
        loading="lazy"
      />
    );
  }

  return (
    <BrandedPlaceholder
      shape="circle"
      initials={initials}
      className={cn(sizeCls, ring, 'flex-shrink-0', className)}
    />
  );
}
